import { useMemo, useState } from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { DateShortcutField } from '@/components/forms/DateShortcutField';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { formatDateOnly } from '@/lib/dateShortcuts';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { formatNumber } from '@/lib/locale';
import { useAiUsage } from '@/hooks/useAiAssistantSettings';
import type { AiUsageRow } from '@/services/aiAssistantSettingsService';
import { sectionHeadingSx } from '@/pages/core/sectionHeadingSx';

const COST_FORMAT: Intl.NumberFormatOptions = { minimumFractionDigits: 2, maximumFractionDigits: 4 };
const TOTAL_ROW_ID = 'total';

/** A report row as the grid shows it: keyed, with the cost already spelled out. */
interface UsageGridRow extends Omit<AiUsageRow, 'provider'> {
  id: string;
  provider: string;
  costText: string;
}

function firstOfThisMonth(): string {
  const now = new Date();
  return formatDateOnly(new Date(now.getFullYear(), now.getMonth(), 1));
}

function formatCost(amount: number, currency: string | undefined): string {
  return `${formatNumber(amount, COST_FORMAT)} ${currency ?? ''}`.trim();
}

/** The rows' cost summed per billing currency, in the order the currencies first appear. */
function costByCurrency(rows: AiUsageRow[]): string {
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (row.cost == null || !row.currency) continue;
    totals.set(row.currency, (totals.get(row.currency) ?? 0) + row.cost);
  }
  return [...totals].map(([currency, amount]) => formatCost(amount, currency)).join(' · ');
}

function sum(rows: AiUsageRow[], pick: (row: AiUsageRow) => number): number {
  return rows.reduce((total, row) => total + pick(row), 0);
}

function toGridRow(row: AiUsageRow, unpriced: (count: number) => string): UsageGridRow {
  const cost = row.cost != null ? formatCost(row.cost, row.currency) : '–';
  return {
    ...row,
    id: `${row.userId ?? ''}|${row.provider}|${row.model}`,
    costText: row.unpricedTurns > 0 ? `${cost} (${unpriced(row.unpricedTurns)})` : cost,
  };
}

function totalsRow(rows: AiUsageRow[], label: string): UsageGridRow {
  return {
    id: TOTAL_ROW_ID,
    userName: label,
    provider: '',
    model: '',
    turns: sum(rows, (r) => r.turns),
    llmCalls: sum(rows, (r) => r.llmCalls),
    inputTokens: sum(rows, (r) => r.inputTokens),
    outputTokens: sum(rows, (r) => r.outputTokens),
    unpricedTurns: 0,
    costText: costByCurrency(rows),
  };
}

/**
 * What the assistant's questions cost in the caller's scope over a period: one row per user,
 * provider and model with the questions, the LLM calls they took, the tokens and the list-price
 * cost, and a pinned total with the cost per billing currency. The period defaults to the current
 * month so far; a `to` given as a date covers that whole day.
 */
export function AiUsageSection() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(firstOfThisMonth);
  const [to, setTo] = useState(() => formatDateOnly(new Date()));
  const { data: report, isFetching, isError } = useAiUsage(from, to);

  const rows = useMemo(
    () => (report?.rows ?? []).map((row) => toGridRow(row, (count) => t('aiSettings.usageUnpriced', { count }))),
    [report, t],
  );
  const pinnedRows = useMemo(
    () => (report && report.rows.length > 0 ? { bottom: [totalsRow(report.rows, t('aiSettings.usageTotal'))] } : undefined),
    [report, t],
  );

  const number = (field: keyof UsageGridRow, headerName: string, width: number): GridColDef<UsageGridRow> => ({
    field,
    headerName,
    type: 'number',
    width,
    valueFormatter: (value: number) => formatNumber(value),
  });
  const columns: GridColDef<UsageGridRow>[] = [
    {
      field: 'userName',
      headerName: t('aiSettings.usageUser'),
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => row.userName ?? t('aiSettings.usageUnknownUser'),
    },
    {
      field: 'provider',
      headerName: t('aiSettings.provider'),
      width: 120,
      valueFormatter: (value: string) => (value ? t(`aiSettings.provider${value}`, { defaultValue: value }) : ''),
    },
    { field: 'model', headerName: t('aiSettings.model'), flex: 1.2, minWidth: 180 },
    number('turns', t('aiSettings.usageTurns'), 100),
    number('llmCalls', t('aiSettings.usageCalls'), 100),
    number('inputTokens', t('aiSettings.usageInputTokens'), 120),
    number('outputTokens', t('aiSettings.usageOutputTokens'), 120),
    { field: 'costText', headerName: t('aiSettings.usageCost'), align: 'right', headerAlign: 'right', width: 220 },
  ];

  return (
    <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1.5 }} elevation={1}>
      <Typography variant="subtitle2" gutterBottom fontWeight={600} sx={{ mb: 0.5, ...sectionHeadingSx }}>
        {t('aiSettings.usageTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{t('aiSettings.usageHelp')}</Typography>
      <JsonFormsThemeProvider density="compact" variant="standard">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Box sx={{ width: 180 }}>
            <DateShortcutField label={t('aiSettings.usageFrom')} value={from} onChange={(v) => setFrom(v ?? '')} />
          </Box>
          <Box sx={{ width: 180 }}>
            <DateShortcutField label={t('aiSettings.usageTo')} value={to} onChange={(v) => setTo(v ?? '')} />
          </Box>
        </Box>
      </JsonFormsThemeProvider>
      {isError && <Alert severity="error" sx={{ mb: 1 }}>{t('aiSettings.usageLoadFailed')}</Alert>}
      <Box sx={{ flex: 1, minHeight: 320, display: 'flex', flexDirection: 'column' }}>
        <DataGridPremium
          density="compact"
          rowHeight={GRID_ROW_HEIGHT}
          rows={rows}
          columns={columns}
          pinnedRows={pinnedRows}
          loading={isFetching}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: t('aiSettings.usageEmpty') }}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '& .MuiDataGrid-main': { flex: 1, overflow: 'auto' },
            '& .MuiDataGrid-virtualScroller': { flex: 1, overflow: 'auto' },
            '& .MuiDataGrid-pinnedRows .MuiDataGrid-cell': { fontWeight: 600 },
          }}
        />
      </Box>
    </Paper>
  );
}
